import React, { useState, useEffect } from "react";
import { ChromePicker } from "react-color";
import { useAuth0 } from "@auth0/auth0-react";

const API_URL = process.env.REACT_APP_API_BASE_URL;

const BannerCreator = () => {
  const [backgroundColor, setBackgroundColor] = useState("#ffffff");
  const [textColor, setTextColor] = useState("#000000");
  const [buttonColor, setButtonColor] = useState("#133b4b");
  const [logo, setLogo] = useState(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [htmlSnippet, setHtmlSnippet] = useState("");
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState("");
  const [loadingDomains, setLoadingDomains] = useState(true);
  const [activePicker, setActivePicker] = useState(null);
  const [copied, setCopied] = useState(false);
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        const response = await fetch(`${API_URL}/user-domains-test`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        const data = await response.json();
        if (data.domains && Array.isArray(data.domains)) {
          const domainList = data.domains.map((item) => item.domain);
          setDomains(domainList);
          if (domainList.length > 0) {
            setSelectedDomain(domainList[0]);
          }
        }
      } catch (error) {
        console.error("Error fetching domains:", error);
      } finally {
        setLoadingDomains(false);
      }
    };

    fetchDomains();
  }, [getAccessTokenSilently]);

  const handleColorChange = (color, type) => {
    if (type === "background") setBackgroundColor(color.hex);
    if (type === "text") setTextColor(color.hex);
    if (type === "button") setButtonColor(color.hex);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogo(file);
    setLogoUrl(URL.createObjectURL(file));
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(htmlSnippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const togglePicker = (picker) => {
    setActivePicker(activePicker === picker ? null : picker);
  };

  const handleGenerateBanner = async () => {
    const formData = new FormData();
    formData.append("backgroundColor", backgroundColor);
    formData.append("textColor", textColor);
    formData.append("buttonColor", buttonColor);
    formData.append("domain", selectedDomain);
    if (logo) formData.append("logo", logo);

    try {
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(`${API_URL}/generate-banner`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();
      const { cssUrl, jsUrl, logoUrln } = data;

      const logoHtml = logoUrln
        ? `<a id="cmp-banner-powered" href="#" rel="noopener noreferrer">
            <img alt="Site logo" src="${logoUrln}" />
           </a>`
        : "";

      setHtmlSnippet(`<link rel="stylesheet" href="${cssUrl}">
<script src="${jsUrl}" defer></script>
<div id="cmp-banner">
  <div class="cmp-banner-inner">
    <div class="cmp-banner-info">
      <div class="cmp-banner-title">
        <span>Denne nettsiden bruker cookies.</span>
      </div>
      <div class="cmp-banner-description">
        <span>Vi bruker cookies for å spore hvilke handlinger og sider du besøker hos oss.</span>
        <a class="cmp-banner-link" href="/personvern" rel="noopener noreferrer">
          <span>Les mer om hvordan vi bruker cookies.</span>
        </a>
      </div>
    </div>
    <div class="cmp-banner-actions">
      <button id="cmp-banner-consent">OK</button>
      <a id="cmp-banner-decline" href="#">Kun nødvendige</a>
      ${logoHtml}
    </div>
  </div>
</div>`);
    } catch (error) {
      console.error("Error generating banner:", error);
    }
  };

  const colorOptions = [
    { key: "background", label: "Bakgrunnsfarge", value: backgroundColor },
    { key: "text", label: "Tekstfarge", value: textColor },
    { key: "button", label: "Knappfarge", value: buttonColor },
  ];

  return (
    <div className="banner-creator">
      <h1>Lag ditt Cookie Banner</h1>
      <p className="bc-subtitle">
        Tilpass utseendet og generer koden for banneret ditt.
      </p>

      {/* Domain Selection */}
      <div className="bc-section">
        <div className="bc-section-title">Velg domene</div>
        {loadingDomains ? (
          <p className="bc-empty-state">Laster domener...</p>
        ) : domains.length === 0 ? (
          <p className="bc-empty-state">Ingen domener tilgjengelig</p>
        ) : (
          <select
            className="bc-select"
            value={selectedDomain}
            onChange={(e) => setSelectedDomain(e.target.value)}
          >
            {domains.map((domain) => (
              <option key={domain} value={domain}>
                {domain}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Color Pickers */}
      <div className="bc-section">
        <div className="bc-section-title">Tilpass farger</div>
        <div className="bc-colors-grid">
          {colorOptions.map(({ key, label, value }) => (
            <div className="bc-color-item" key={key}>
              <span className="bc-color-label">{label}</span>
              <div className="bc-color-swatch-row">
                <div
                  className={`bc-color-swatch${
                    activePicker === key ? " active" : ""
                  }`}
                  style={{ backgroundColor: value }}
                  onClick={() => togglePicker(key)}
                />
                <span className="bc-color-hex">{value}</span>
              </div>
              {activePicker === key && (
                <div className="bc-picker-wrapper">
                  <ChromePicker
                    color={value}
                    onChange={(color) => handleColorChange(color, key)}
                    disableAlpha
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Logo Upload */}
      <div className="bc-section">
        <div className="bc-section-title">Logo (valgfritt)</div>
        <div className="bc-logo-section">
          <label className="bc-upload-btn">
            Velg fil
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              style={{ display: "none" }}
            />
          </label>
          <span className="bc-upload-hint">.svg anbefalt</span>
          {logoUrl && (
            <img className="bc-logo-preview" src={logoUrl} alt="Logo" />
          )}
        </div>
      </div>

      {/* Live Preview */}
      <div className="bc-section">
        <div className="bc-section-title">Forhåndsvisning</div>
        <div
          className="bc-preview-container"
          style={{ backgroundColor, color: textColor }}
        >
          <div className="bc-preview-inner">
            <div className="bc-preview-title">
              Denne nettsiden bruker cookies.
            </div>
            <div className="bc-preview-description">
              Vi bruker cookies for å spore hvilke handlinger og sider du
              besøker hos oss.{" "}
              <span className="bc-preview-link" style={{ color: textColor }}>
                Les mer om hvordan vi bruker cookies.
              </span>
            </div>
            <div className="bc-preview-actions">
              <button
                className="bc-preview-consent-btn"
                style={{
                  backgroundColor: buttonColor,
                  color: backgroundColor,
                }}
              >
                OK
              </button>
              <button
                className="bc-preview-decline-btn"
                style={{
                  borderColor: buttonColor,
                  color: buttonColor,
                }}
              >
                Kun nødvendige
              </button>
              {logoUrl && (
                <span className="bc-preview-logo">
                  <img src={logoUrl} alt="Site logo" />
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Generate Button */}
      <div className="bc-actions">
        <button className="bc-generate-btn" onClick={handleGenerateBanner}>
          Generer Banner
        </button>
      </div>

      {/* HTML Snippet Output */}
      {htmlSnippet && (
        <div className="bc-section bc-snippet-section">
          <div className="bc-snippet-header">
            <h2>HTML Snippet</h2>
            <button
              className={`bc-copy-btn${copied ? " copied" : ""}`}
              onClick={copyToClipboard}
            >
              {copied ? "Kopiert!" : "Kopier"}
            </button>
          </div>
          <pre className="bc-snippet-code">
            <code>{htmlSnippet}</code>
          </pre>
        </div>
      )}
    </div>
  );
};

export default BannerCreator;
